import { Dialog, Transition } from "@headlessui/react";
import { usePage } from "@inertiajs/react";
import { Dispatch, Fragment, SetStateAction } from "react";

import { DistributorExpenseData } from "@/components/Cards/BillsInfo/types";
import Typography from "@/components/Typography";
import moneyFormatter from "@/utils/moneyFormat";
import numberFormatter from "@/utils/numberFormatter";

interface CalculationDetailsModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  subRows: DistributorExpenseData[];
  month: string;
  discountPercentage: number;
  displayedValue?: string;
}

export default function CalculationDetailsModal({
  isOpen,
  setIsOpen,
  subRows,
  month,
  discountPercentage,
  displayedValue,
}: CalculationDetailsModalProps) {
  const { currentUser } = usePage().props as any;
  const userRole = currentUser?.role || "consumer";
  const isManager =
    userRole === "manager" || userRole === "admin" || userRole === "investment_manager";
  function closeModal() {
    setIsOpen(false);
  }

  // ==============================================================================
  // CALCULA INFORMAÇÕES DE MODIFICAÇÃO se necessário
  // ==============================================================================
  const getModificationInfo = () => {
    if (!displayedValue || !subRows || subRows.length === 0) return null;

    const expected_value = subRows.reduce((sum, row) => sum + (row.price || 0), 0);
    const cleanDisplay = String(displayedValue)
      .replace(/[R$\s]/g, "")
      .replace(/\./g, "")
      .replace(",", ".");
    const displayValueNum = Number(cleanDisplay);
    const diff = displayValueNum - expected_value;
    const tolerance = 1.0; // Tolerância de 1 real

    // Se a diferença estiver dentro da tolerância, considera igual
    const actual_diff = Math.abs(diff) <= tolerance ? 0 : diff;

    return {
      display_value: displayValueNum,
      expected_value,
      diff: actual_diff,
      is_higher: actual_diff > 0,
    };
  };

  const modificationInfo = getModificationInfo();

  // Verifica se houve modificação real (diferença maior que tolerância)
  const hasModification = modificationInfo && modificationInfo.diff !== 0;

  // Helper function to calculate detailed values for GD1
  const calculateGD1Details = () => {
    const gd1Rows = subRows.filter((row) => row.is_gd1);
    if (gd1Rows.length === 0) return null;

    let totalEnergy = 0;
    let totalDistributorPrice = 0;
    let totalTusdIcms = 0;
    let totalCompensableTariff = 0;
    let totalWithoutDiscount = 0;
    let totalDiscount = 0;
    let totalWithDiscount = 0;

    gd1Rows.forEach((row) => {
      const energy = row.compensated_kwh_gd1;
      const distributorPrice = row.distributor_kwh_price;
      const tusdIcms = row.tusd_icms_cents;
      const compensableTariff = distributorPrice - tusdIcms;
      const subtotal = compensableTariff * energy;
      const discount = subtotal * discountPercentage;
      const final = subtotal - discount;

      totalEnergy += energy;
      totalDistributorPrice += distributorPrice * energy;
      totalTusdIcms += tusdIcms * energy;
      totalCompensableTariff += compensableTariff * energy;
      totalWithoutDiscount += subtotal;
      totalDiscount += discount;
      totalWithDiscount += final;
    });

    const averageCompensableTariff = totalEnergy > 0 ? totalCompensableTariff / totalEnergy : 0;

    return {
      totalEnergy,
      totalDistributorPrice,
      totalTusdIcms,
      averageCompensableTariff,
      totalWithoutDiscount,
      totalDiscount,
      totalWithDiscount,
    };
  };

  // Helper function to calculate detailed values for GD2
  const calculateGD2Details = () => {
    const gd2Rows = subRows.filter((row) => row.is_gd2);
    if (gd2Rows.length === 0) return null;

    let totalEnergy = 0;
    let totalDistributorPrice = 0;
    let totalTusdIcms = 0;
    let totalFiob = 0;
    let totalIcmsRefund = 0;
    let totalCompensableTariff = 0;
    let totalBeforeGeedeeDiscount = 0;
    let totalAfterIcmsRefund = 0;
    let totalGeedeeDiscount = 0;
    let totalFinal = 0;

    gd2Rows.forEach((row) => {
      const energy = row.compensated_kwh_gd2;
      const distributorPrice = row.distributor_kwh_price;
      const tusdIcms = row.tusd_icms_cents;
      const fiobCost = row.fiob_cost_cents;
      const icmsRefundTariff = row.icms_refund_amount || 0;
      const compensableTariff = distributorPrice - tusdIcms - fiobCost - icmsRefundTariff;
      const subtotalBeforeGeedee = compensableTariff * energy;
      const subtotalAfterIcmsRefund = subtotalBeforeGeedee;
      const geedeeDiscount = subtotalAfterIcmsRefund * discountPercentage;
      const finalValue = subtotalAfterIcmsRefund - geedeeDiscount;

      totalEnergy += energy;
      totalDistributorPrice += distributorPrice * energy;
      totalTusdIcms += tusdIcms * energy;
      totalFiob += fiobCost * energy;
      totalIcmsRefund += icmsRefundTariff * energy;
      totalCompensableTariff += compensableTariff * energy;
      totalBeforeGeedeeDiscount += subtotalBeforeGeedee;
      totalAfterIcmsRefund += subtotalAfterIcmsRefund;
      totalGeedeeDiscount += geedeeDiscount;
      totalFinal += finalValue;
    });

    const averageCompensableTariff = totalEnergy > 0 ? totalCompensableTariff / totalEnergy : 0;

    return {
      totalEnergy,
      totalDistributorPrice,
      totalTusdIcms,
      totalFiob,
      totalIcmsRefund,
      averageCompensableTariff,
      totalBeforeGeedeeDiscount,
      totalAfterIcmsRefund,
      totalGeedeeDiscount,
      totalFinal,
    };
  };

  const gd1Details = calculateGD1Details();
  const gd2Details = calculateGD2Details();

  const totalFinal = (gd1Details?.totalWithDiscount || 0) + (gd2Details?.totalFinal || 0);

  // Se foi modificado e é consumer, mostra apenas aviso
  if (hasModification && !isManager) {
    return (
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-70"
            leave="ease-in duration-200"
            leaveFrom="opacity-70"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/75" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl overflow-hidden rounded-xl bg-white text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="flex items-center gap-2 rounded-t-xl bg-primary500 px-5 py-4 text-sm font-semibold leading-6 text-white"
                  >
                    Demonstrativo de Cálculo - {month}
                  </Dialog.Title>
                  <div className="p-5">
                    <div className="bg-primary50 flex flex-col rounded-lg border border-primary300 p-4">
                      <Typography variant="h6" color="primary500" className="mb-4">
                        Atenção: Valores Modificados Manualmente
                      </Typography>

                      <Typography variant="pSmall" color="black">
                        Não foi possivel exibir os valores corretos. Os valores exibidos nesta conta
                        foram ajustados manualmente. Para mais informações, entre em contato pelo
                        e-mail{" "}
                        <a
                          href="mailto:contato@geedee.com.br"
                          className="text-primary500 underline"
                        >
                          contato@geedee.com.br
                        </a>
                      </Typography>
                    </div>
                  </div>

                  <div className="flex justify-end border-t p-4">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-primary500 px-6 py-2 text-sm font-medium text-white hover:bg-primary400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary500 focus-visible:ring-offset-2"
                      onClick={closeModal}
                    >
                      Fechar
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-70"
            leave="ease-in duration-200"
            leaveFrom="opacity-70"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/75" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-5xl overflow-hidden rounded-xl bg-white text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="flex items-center gap-2 rounded-t-xl bg-primary500 px-5 py-4 text-sm font-semibold leading-6 text-white"
                  >
                    Demonstrativo Detalhado do Cálculo - {month}
                  </Dialog.Title>
                  <div className="max-h-[70vh] overflow-y-auto p-5">
                    <div className="space-y-3">
                      {/* AVISO DE MODIFICAÇÃO MANUAL */}
                      {hasModification && (
                        <div className="flex flex-col px-4">
                          <Typography variant="h6" color="primary500" className="mb-1">
                            Atenção: Valores Modificados Manualmente
                          </Typography>

                          <Typography variant="pSmall" color="primary500" className="mb-4">
                            Quando os valores são alterados manualmente o sistema não exibe essa
                            modal para o Consumidor.
                          </Typography>
                        </div>
                      )}

                      {/* INTRODUÇÃO */}
                      <div className="bg-primary50 flex flex-col rounded-lg border border-primary300 p-4">
                        <Typography variant="h6" color="primary500" className="mb-2">
                          Entenda seu cálculo
                        </Typography>
                        <Typography variant="pSmall" color="black">
                          Este demonstrativo mostra o cálculo consolidado de todas as suas
                          distribuidoras/facilities. Cada etapa é detalhada para que você entenda
                          exatamente como seu valor é calculado.
                        </Typography>
                      </div>

                      {/* GD1 - PASSO A PASSO DETALHADO */}
                      {gd1Details && (
                        <div className="rounded-lg border border-primary300 bg-white p-5">
                          <Typography variant="h6" color="primary500" className="mb-4">
                            Cálculo GD1 - Geradora Distribuída 1
                          </Typography>

                          <div className="space-y-3">
                            {/* PASSO 1: Mostrar tarifas de cada distribuidora */}
                            <div className="rounded-lg bg-grey100 p-3">
                              <Typography
                                variant="btnMedium"
                                color="black"
                                className="mb-3 font-semibold"
                              >
                                Passo 1: Tarifas das distribuidoras
                              </Typography>

                              {subRows
                                .filter((row) => row.is_gd1)
                                .map((distributor, idx) => {
                                  const energy = distributor.compensated_kwh_gd1;
                                  const distributorPrice = distributor.distributor_kwh_price;
                                  const tusdIcms = distributor.tusd_icms_cents;
                                  const compensableTariff = distributorPrice - tusdIcms;

                                  return (
                                    <div
                                      key={distributor.id}
                                      className="mb-3 rounded p-2.5 last:mb-0"
                                    >
                                      <Typography
                                        variant="pSmall"
                                        color="primary500"
                                        className="mb-2 font-semibold"
                                      >
                                        Distribuidora {idx + 1}
                                      </Typography>

                                      <div className="space-y-2 pl-4">
                                        <div className="flex justify-between border-b border-grey200 pb-2">
                                          <Typography variant="pSmall" color="black">
                                            a) Tarifa com impostos:
                                          </Typography>
                                          <Typography variant="pSmall" color="black">
                                            {moneyFormatter(distributorPrice)} / kWh
                                          </Typography>
                                        </div>
                                        <div className="flex justify-between border-b border-grey200 pb-2">
                                          <Typography variant="pSmall" color="black">
                                            b) ICMS da TUSD:
                                          </Typography>
                                          <Typography variant="pSmall" color="danger300">
                                            {moneyFormatter(tusdIcms)} / kWh
                                          </Typography>
                                        </div>
                                        <div className="flex justify-between border-b border-grey200 pb-2">
                                          <Typography variant="pSmall" color="black">
                                            c) Cálculo: Tarifa - ICMS
                                          </Typography>
                                          <Typography variant="pSmall" color="black">
                                            {moneyFormatter(distributorPrice)} -{" "}
                                            {moneyFormatter(tusdIcms)} ={" "}
                                            {moneyFormatter(compensableTariff)} / kWh
                                          </Typography>
                                        </div>
                                        <div className="flex justify-between border-b border-grey200 pb-2">
                                          <Typography
                                            variant="pSmall"
                                            color="primary500"
                                            className="font-semibold"
                                          >
                                            d) Tarifa Compensável:
                                          </Typography>
                                          <Typography
                                            variant="pSmall"
                                            color="primary500"
                                            className="font-semibold"
                                          >
                                            {moneyFormatter(compensableTariff)} / kWh
                                          </Typography>
                                        </div>
                                        <div className="flex justify-between">
                                          <Typography variant="pSmall" color="black">
                                            e) Energia injetada:
                                          </Typography>
                                          <Typography variant="pSmall" color="black">
                                            {numberFormatter(energy)} kWh
                                          </Typography>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>

                            {/* PASSO 2: Cálculo consolidado */}
                            <div className="rounded-lg bg-grey100 p-3">
                              <Typography
                                variant="btnMedium"
                                color="black"
                                className="mb-3 font-semibold"
                              >
                                Passo 2: Cálculo consolidado GD1
                              </Typography>

                              <div className="space-y-2">
                                <div className="flex justify-between border-b border-grey300 pb-2">
                                  <Typography variant="pSmall" color="black">
                                    Energia total compensada (GD1):
                                  </Typography>
                                  <Typography variant="pSmall" color="black">
                                    {numberFormatter(gd1Details.totalEnergy)} kWh
                                  </Typography>
                                </div>

                                <div className="flex justify-between border-b border-grey300 pb-2">
                                  <Typography variant="pSmall" color="black">
                                    Total Tarifa Distribuidora:
                                  </Typography>
                                  <Typography variant="pSmall" color="black">
                                    {moneyFormatter(gd1Details.totalDistributorPrice)}
                                  </Typography>
                                </div>

                                <div className="flex justify-between border-b border-grey300 pb-2">
                                  <Typography variant="pSmall" color="black">
                                    (-) Total ICMS da TUSD:
                                  </Typography>
                                  <Typography variant="pSmall" color="danger300">
                                    -{moneyFormatter(gd1Details.totalTusdIcms)}
                                  </Typography>
                                </div>

                                <div className="flex justify-between border-b border-grey300 pb-2">
                                  <Typography variant="pSmall" color="black">
                                    Tarifa Compensável Média:
                                  </Typography>
                                  <Typography variant="pSmall" color="primary500">
                                    {moneyFormatter(gd1Details.averageCompensableTariff)} / kWh
                                  </Typography>
                                </div>

                                <div className="bg-primary50 rounded-lg p-3">
                                  <Typography
                                    variant="pSmall"
                                    color="black"
                                    className="mb-1 font-semibold"
                                  >
                                    Fórmula do cálculo
                                  </Typography>
                                  <Typography variant="pSmall" color="black" className="mb-2">
                                    Subtotal = Tarifa Compensável Média × Energia Total
                                  </Typography>
                                  <div className="flex justify-between">
                                    <Typography variant="pSmall" color="black">
                                      {moneyFormatter(gd1Details.averageCompensableTariff)} ×{" "}
                                      {numberFormatter(gd1Details.totalEnergy)} kWh =
                                    </Typography>
                                    <Typography
                                      variant="btnMedium"
                                      color="primary500"
                                      className="font-bold"
                                    >
                                      {moneyFormatter(gd1Details.totalWithoutDiscount)}
                                    </Typography>
                                  </div>
                                </div>

                                <div className="flex justify-between border-b border-grey300 pb-2">
                                  <Typography
                                    variant="btnMedium"
                                    color="black"
                                    className="font-semibold"
                                  >
                                    Subtotal sem desconto
                                  </Typography>
                                  <Typography
                                    variant="btnMedium"
                                    color="black"
                                    className="font-semibold"
                                  >
                                    {moneyFormatter(gd1Details.totalWithoutDiscount)}
                                  </Typography>
                                </div>

                                <div className="bg-yellow50 flex flex-col gap-1 rounded-lg p-3">
                                  <Typography
                                    variant="pSmall"
                                    color="black"
                                    className="mb-1 font-semibold"
                                  >
                                    Desconto Geedee
                                  </Typography>
                                  <Typography variant="pSmall" color="black" className="mb-2">
                                    Desconto = Subtotal × {(discountPercentage * 100).toFixed(1)}%
                                  </Typography>
                                  <div className="flex justify-between">
                                    <Typography variant="pSmall" color="black">
                                      {moneyFormatter(gd1Details.totalWithoutDiscount)} ×{" "}
                                      {(discountPercentage * 100).toFixed(1)}% =
                                    </Typography>
                                    <Typography
                                      variant="btnMedium"
                                      color="danger300"
                                      className="font-bold"
                                    >
                                      -{moneyFormatter(gd1Details.totalDiscount)}
                                    </Typography>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between gap-4 rounded-lg bg-primary500 px-4 py-3">
                                  <Typography variant="btnLarge" color="white">
                                    Total GD1 com desconto
                                  </Typography>
                                  <Typography variant="h6" color="white" className="shrink-0">
                                    {moneyFormatter(gd1Details.totalWithDiscount)}
                                  </Typography>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* GD2 - PASSO A PASSO DETALHADO */}
                      {gd2Details && (
                        <div className="rounded-lg border border-primary300 p-5">
                          <Typography variant="h6" color="primary500" className="mb-4">
                            Cálculo GD2 - Geradora Distribuída 2
                          </Typography>

                          <div className="space-y-3">
                            {/* PASSO 1: Mostrar tarifas de cada distribuidora */}
                            <div className="rounded-lg bg-grey100 p-3">
                              <Typography
                                variant="btnMedium"
                                color="black"
                                className="mb-3 font-semibold"
                              >
                                Passo 1: Tarifas das distribuidoras
                              </Typography>

                              {subRows
                                .filter((row) => row.is_gd2)
                                .map((distributor, idx) => {
                                  const energy = distributor.compensated_kwh_gd2;
                                  const distributorPrice = distributor.distributor_kwh_price;
                                  const tusdIcms = distributor.tusd_icms_cents;
                                  const fiobCost = distributor.fiob_cost_cents;
                                  const icmsRefundTariff = distributor.icms_refund_amount || 0;
                                  const compensableTariff =
                                    distributorPrice - tusdIcms - fiobCost - icmsRefundTariff;

                                  return (
                                    <div
                                      key={distributor.id}
                                      className="mb-3 rounded p-2.5 last:mb-0"
                                    >
                                      <Typography
                                        variant="pSmall"
                                        color="primary500"
                                        className="mb-2 font-semibold"
                                      >
                                        Distribuidora {idx + 1}
                                      </Typography>

                                      <div className="space-y-2 pl-4">
                                        <div className="flex justify-between border-b border-grey200 pb-2">
                                          <Typography variant="pSmall" color="black">
                                            a) Tarifa com impostos:
                                          </Typography>
                                          <Typography variant="pSmall" color="black">
                                            {moneyFormatter(distributorPrice)} / kWh
                                          </Typography>
                                        </div>
                                        <div className="flex justify-between border-b border-grey200 pb-2">
                                          <Typography variant="pSmall" color="black">
                                            b) ICMS da TUSD:
                                          </Typography>
                                          <Typography variant="pSmall" color="danger300">
                                            {moneyFormatter(tusdIcms)} / kWh
                                          </Typography>
                                        </div>
                                        <div className="flex justify-between border-b border-grey200 pb-2">
                                          <Typography variant="pSmall" color="black">
                                            c) Custo FIOB:
                                          </Typography>
                                          <Typography variant="pSmall" color="danger300">
                                            {moneyFormatter(fiobCost)} / kWh
                                          </Typography>
                                        </div>
                                        <div className="flex justify-between border-b border-grey200 pb-2">
                                          <Typography variant="pSmall" color="black">
                                            d) ICMS Subvenção CDE:
                                          </Typography>
                                          <Typography variant="pSmall" color="danger300">
                                            {moneyFormatter(icmsRefundTariff)} / kWh
                                          </Typography>
                                        </div>
                                        <div className="flex justify-between border-b border-grey200 pb-2">
                                          <Typography variant="pSmall" color="black">
                                            e) Cálculo: Tarifa - ICMS - FIOB - Subvenção
                                          </Typography>
                                          <Typography variant="pSmall" color="black">
                                            {moneyFormatter(distributorPrice)} -{" "}
                                            {moneyFormatter(tusdIcms)} - {moneyFormatter(fiobCost)}{" "}
                                            - {moneyFormatter(icmsRefundTariff)} ={" "}
                                            {moneyFormatter(compensableTariff)} / kWh
                                          </Typography>
                                        </div>
                                        <div className="flex justify-between border-b border-grey200 pb-2">
                                          <Typography
                                            variant="pSmall"
                                            color="primary500"
                                            className="font-semibold"
                                          >
                                            f) Tarifa Compensável:
                                          </Typography>
                                          <Typography
                                            variant="pSmall"
                                            color="primary500"
                                            className="font-semibold"
                                          >
                                            {moneyFormatter(compensableTariff)} / kWh
                                          </Typography>
                                        </div>
                                        <div className="flex justify-between">
                                          <Typography variant="pSmall" color="black">
                                            g) Energia injetada:
                                          </Typography>
                                          <Typography variant="pSmall" color="black">
                                            {numberFormatter(energy)} kWh
                                          </Typography>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>

                            {/* PASSO 2: Cálculo consolidado */}
                            <div className="rounded-lg bg-grey100 p-3">
                              <Typography
                                variant="btnMedium"
                                color="black"
                                className="mb-3 font-semibold"
                              >
                                Passo 2: Cálculo consolidado GD2
                              </Typography>

                              <div className="space-y-2">
                                <div className="flex justify-between border-b border-grey300 pb-2">
                                  <Typography variant="pSmall" color="black">
                                    Energia total compensada (GD2):
                                  </Typography>
                                  <Typography variant="pSmall" color="black">
                                    {numberFormatter(gd2Details.totalEnergy)} kWh
                                  </Typography>
                                </div>

                                <div className="flex justify-between border-b border-grey300 pb-2">
                                  <Typography variant="pSmall" color="black">
                                    Total Tarifa Distribuidora:
                                  </Typography>
                                  <Typography variant="pSmall" color="black">
                                    {moneyFormatter(gd2Details.totalDistributorPrice)}
                                  </Typography>
                                </div>

                                <div className="flex justify-between border-b border-grey300 pb-2">
                                  <Typography variant="pSmall" color="black">
                                    (-) Total ICMS da TUSD:
                                  </Typography>
                                  <Typography variant="pSmall" color="danger300">
                                    -{moneyFormatter(gd2Details.totalTusdIcms)}
                                  </Typography>
                                </div>

                                <div className="flex justify-between border-b border-grey300 pb-2">
                                  <Typography variant="pSmall" color="black">
                                    (-) Total Custo FIOB:
                                  </Typography>
                                  <Typography variant="pSmall" color="danger300">
                                    -{moneyFormatter(gd2Details.totalFiob)}
                                  </Typography>
                                </div>

                                {gd2Details.totalIcmsRefund > 0 && (
                                  <div className="flex justify-between border-b border-grey300 pb-2">
                                    <Typography variant="pSmall" color="black">
                                      (-) Total ICMS Subvenção CDE:
                                    </Typography>
                                    <Typography variant="pSmall" color="danger300">
                                      -{moneyFormatter(gd2Details.totalIcmsRefund)}
                                    </Typography>
                                  </div>
                                )}

                                <div className="flex justify-between border-b border-grey300 pb-2">
                                  <Typography variant="pSmall" color="black">
                                    Tarifa Compensável Média:
                                  </Typography>
                                  <Typography variant="pSmall" color="primary500">
                                    {moneyFormatter(gd2Details.averageCompensableTariff)} / kWh
                                  </Typography>
                                </div>

                                <div className="bg-primary50 rounded-lg p-3">
                                  <Typography
                                    variant="pSmall"
                                    color="black"
                                    className="mb-1 font-semibold"
                                  >
                                    Fórmula do cálculo
                                  </Typography>
                                  <Typography variant="pSmall" color="black" className="mb-2">
                                    Subtotal = Tarifa Compensável Média × Energia Total
                                  </Typography>
                                  <div className="flex justify-between">
                                    <Typography variant="pSmall" color="black">
                                      {moneyFormatter(gd2Details.averageCompensableTariff)} ×{" "}
                                      {numberFormatter(gd2Details.totalEnergy)} kWh =
                                    </Typography>
                                    <Typography
                                      variant="btnMedium"
                                      color="primary500"
                                      className="font-bold"
                                    >
                                      {moneyFormatter(gd2Details.totalBeforeGeedeeDiscount)}
                                    </Typography>
                                  </div>
                                </div>

                                <div className="flex justify-between border-b border-grey300 pb-2">
                                  <Typography
                                    variant="btnMedium"
                                    color="black"
                                    className="font-semibold"
                                  >
                                    Subtotal sem desconto Geedee
                                  </Typography>
                                  <Typography
                                    variant="btnMedium"
                                    color="black"
                                    className="font-semibold"
                                  >
                                    {moneyFormatter(gd2Details.totalAfterIcmsRefund)}
                                  </Typography>
                                </div>

                                <div className="bg-yellow50 flex flex-col gap-2 rounded-lg p-3">
                                  <Typography
                                    variant="pSmall"
                                    color="black"
                                    className="mb-1 font-semibold"
                                  >
                                    Desconto Geedee
                                  </Typography>
                                  <Typography variant="pSmall" color="black" className="mb-2">
                                    Desconto = Subtotal × {(discountPercentage * 100).toFixed(1)}%
                                  </Typography>
                                  <div className="flex justify-between">
                                    <Typography variant="pSmall" color="black">
                                      {moneyFormatter(gd2Details.totalAfterIcmsRefund)} ×{" "}
                                      {(discountPercentage * 100).toFixed(1)}% =
                                    </Typography>
                                    <Typography
                                      variant="btnMedium"
                                      color="danger300"
                                      className="font-bold"
                                    >
                                      -{moneyFormatter(gd2Details.totalGeedeeDiscount)}
                                    </Typography>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between gap-4 rounded-lg bg-primary500 px-4 py-3">
                                  <Typography variant="btnLarge" color="white">
                                    Total GD2 com desconto
                                  </Typography>
                                  <Typography variant="h6" color="white" className="shrink-0">
                                    {moneyFormatter(gd2Details.totalFinal)}
                                  </Typography>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* RESUMO FINAL DETALHADO */}
                      <div className="bg-primary50 rounded-lg border border-primary300 p-5">
                        <Typography variant="h6" color="primary500" className="mb-4">
                          Resumo final consolidado
                        </Typography>
                        <div className="space-y-2.5">
                          {gd1Details && (
                            <div className="flex items-center justify-between gap-4 border-b border-primary300 pb-2">
                              <Typography variant="pSmall" color="black">
                                Total GD1 com desconto
                              </Typography>
                              <Typography variant="btnMedium" color="black" className="shrink-0">
                                {moneyFormatter(gd1Details.totalWithDiscount)}
                              </Typography>
                            </div>
                          )}

                          {gd2Details && (
                            <div className="flex items-center justify-between gap-4 border-b border-primary300 pb-2">
                              <Typography variant="pSmall" color="black">
                                Total GD2 com desconto
                              </Typography>
                              <Typography variant="btnMedium" color="black" className="shrink-0">
                                {moneyFormatter(gd2Details.totalFinal)}
                              </Typography>
                            </div>
                          )}

                          <div className="mt-4 flex items-center justify-between gap-4 rounded-lg bg-primary500 px-4 py-3">
                            <Typography variant="btnLarge" color="white">
                              Valor final a pagar
                            </Typography>
                            <Typography variant="h6" color="white" className="shrink-0">
                              {moneyFormatter(totalFinal)}
                            </Typography>
                          </div>
                        </div>
                      </div>

                      {/* NOTA EXPLICATIVA */}
                      <div className="rounded-lg bg-grey100 p-4">
                        <Typography variant="pSmall" color="grey500">
                          <strong>Como funciona:</strong> Os cálculos acima mostram o passo a passo
                          de como chegamos ao valor final. Para cada distribuidora, calculamos a
                          tarifa compensável (tarifa com impostos menos ICMS da TUSD), multiplicamos
                          pela energia injetada, e aplicamos o desconto Geedee de{" "}
                          {(discountPercentage * 100).toFixed(1)}%. No caso de GD2, também é
                          deduzido o custo FIOB. O valor final é a soma de todos os cálculos.
                        </Typography>
                      </div>

                      {/* COMPARAÇÃO DE VALORES (apenas para manager) */}
                      {hasModification && displayedValue && isManager && modificationInfo && (
                        <div className="bg-primary50 rounded-lg border border-primary300 p-5">
                          <div className="flex flex-col">
                            <Typography variant="h6" color="primary500" className="mb-1">
                              Comparação de valores
                            </Typography>
                            <Typography variant="pSmall" color="danger100" className="mb-4">
                              Só exibido para gestores. Mostra a diferença entre o valor calculado
                              automaticamente pelo sistema e o valor manual modificado (ajustado
                              pela Geedee).
                            </Typography>
                          </div>

                          <div className="space-y-2.5">
                            {/* Valor Calculado Automaticamente */}
                            <div className="flex items-center justify-between gap-4 border-b border-primary300 pb-2">
                              <div>
                                <Typography
                                  variant="btnMedium"
                                  color="black"
                                  className="font-semibold"
                                >
                                  Valor calculado automaticamente
                                </Typography>
                                <Typography variant="pSmall" color="grey500" className="ml-1 mt-1">
                                  (Valor que o sistema calculou automaticamente)
                                </Typography>
                              </div>
                              <Typography
                                variant="btnMedium"
                                color="black"
                                className="shrink-0 font-semibold"
                              >
                                {moneyFormatter(modificationInfo.expected_value)}
                              </Typography>
                            </div>

                            {/* Valor Manual Modificado */}
                            <div className="flex items-center justify-between gap-4 border-b border-primary300 pb-2">
                              <div>
                                <Typography
                                  variant="btnMedium"
                                  color="primary500"
                                  className="font-semibold"
                                >
                                  Valor manual modificado
                                </Typography>
                                <Typography variant="pSmall" color="grey500" className="ml-1 mt-1">
                                  (Valor que está sendo cobrado (ajustado pela Geedee))
                                </Typography>
                              </div>
                              <Typography
                                variant="btnMedium"
                                color="primary500"
                                className="shrink-0 font-bold"
                              >
                                {displayedValue}
                              </Typography>
                            </div>

                            {/* Diferença */}
                            <div className="flex items-center justify-between gap-4 rounded-lg bg-primary500 px-4 py-3">
                              <div>
                                <Typography variant="btnLarge" color="white">
                                  Diferença
                                </Typography>
                              </div>
                              <div className="text-right">
                                <Typography variant="h6" color="white" className="shrink-0">
                                  {modificationInfo.is_higher ? "+" : "-"}
                                  {moneyFormatter(Math.abs(modificationInfo.diff))}
                                </Typography>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end border-t p-4">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-primary500 px-6 py-2 text-sm font-medium text-white hover:bg-primary400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary500 focus-visible:ring-offset-2"
                      onClick={closeModal}
                    >
                      Fechar
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
